import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import AnimatedHeading from '@/components/AnimatedHeading';
import FadeIn from '@/components/FadeIn';

const codeColumns = [
  { left: '8%', dur: 9, delay: 0, text: 'def run():\n  ai.exec()\n  return ok\n> 0x1F\n[macro]\nloop()\nyield()\nbuild++' },
  { left: '22%', dur: 12, delay: 2, text: 'import bot\nwhile 1:\n  crawl()\n  save(db)\n0b1011\n<node/>\nrender()\nqueue' },
  { left: '70%', dur: 10, delay: 1, text: 'export fn\nasync ()=>\n  await api\n  .fetch()\nSHA256\n#!/bin\ncompile\nOK 200' },
  { left: '86%', dur: 13, delay: 3, text: 'model.fit()\nepoch 24\nloss 0.03\n<xlsx>\nsum(A:Z)\ngame.tick\nspawn()\n0xFF' },
];

const appCards = [
  {
    top: '18%',
    left: '6%',
    rot: '-8deg',
    icon: 'ri-robot-2-line',
    title: 'AI Workflow',
    sub: 'auto-pipeline',
    dur: 8,
  },
  {
    top: '58%',
    left: '3%',
    rot: '6deg',
    icon: 'ri-terminal-box-line',
    title: 'macro.exe',
    sub: 'v2.4 · running',
    dur: 9.5,
  },
  {
    top: '22%',
    left: '82%',
    rot: '7deg',
    icon: 'ri-notion-fill',
    title: 'Notion Kit',
    sub: '12 templates',
    dur: 9,
  },
  {
    top: '62%',
    left: '80%',
    rot: '-6deg',
    icon: 'ri-file-excel-2-line',
    title: 'Sheet Tool',
    sub: 'auto-report',
    dur: 8.5,
  },
];

const particles = Array.from({ length: 18 }).map((_, i) => ({
  left: `${(i * 5.4 + 4) % 96}%`,
  bottom: `${(i * 13) % 60}px`,
  size: 2 + (i % 3),
  dur: 6 + (i % 5),
  delay: (i % 6) * 0.7,
  gold: i % 3 === 0,
}));

export default function Hero() {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      const onMove = (e: MouseEvent) => {
        const { innerWidth, innerHeight } = window;
        const x = (e.clientX / innerWidth - 0.5) * 20;
        const y = (e.clientY / innerHeight - 0.5) * 20;
        gsap.to('.hero-parallax', { x, y, duration: 1.2, ease: 'power2.out' });
        gsap.to('.hero-parallax-strong', {
          x: x * 1.8,
          y: y * 1.8,
          duration: 1.2,
          ease: 'power2.out',
        });
      };
      window.addEventListener('mousemove', onMove);
      return () => window.removeEventListener('mousemove', onMove);
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={rootRef}
      className="relative min-h-screen w-full overflow-hidden bg-background-50 text-white"
    >
      {/* Static gradient backdrop — a full-bleed autoplay video used to sit here.
          On mobile it competed with the page's own JS for CPU/GPU during decode,
          which delayed everything timed off setTimeout/IntersectionObserver
          (including this section's own headline animation and anything below
          it on the page). The existing CSS decoration (code streams, rings,
          particles, glass cards) already carries the visual, so the video adds
          weight without much visible payoff. */}
      <div className="absolute inset-0 bg-gradient-to-br from-background-100 via-background-50 to-black" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/35 to-background-50" />

      {/* Falling code streams */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-70">
        {codeColumns.map((c) => (
          <div
            key={c.left}
            className="code-stream"
            style={{ left: c.left, animationDuration: `${c.dur}s`, animationDelay: `${c.delay}s` }}
          >
            {c.text}
          </div>
        ))}
      </div>

      {/* Scanline sweep */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="hero-scanline" />
      </div>

      {/* Parallax tech grid */}
      <div className="hero-parallax absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute inset-0 tech-grid tech-grid-mask" />
      </div>

      {/* Rotating rings + glow orbs */}
      <div className="hero-parallax absolute inset-0 pointer-events-none">
        <div className="hero-ring absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[620px] h-[620px] opacity-60" />
        <div className="hero-ring absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[880px] h-[880px] opacity-30" style={{ animationDuration: '38s', animationDirection: 'reverse' }} />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-primary-500/25 blur-[130px] animate-float-slower" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-accent-500/20 blur-[130px] animate-float-slow" />
      </div>

      {/* Rising particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((p, i) => (
          <span
            key={i}
            className={`hero-particle ${p.gold ? 'bg-accent-400/70' : 'bg-primary-400/70'}`}
            style={{
              left: p.left,
              bottom: p.bottom,
              width: `${p.size}px`,
              height: `${p.size}px`,
              animationDuration: `${p.dur}s`,
              animationDelay: `${p.delay}s`,
              boxShadow: p.gold
                ? '0 0 8px oklch(var(--accent-400) / 0.8)'
                : '0 0 8px oklch(var(--primary-400) / 0.8)',
            }}
          />
        ))}
      </div>

      {/* Floating glass app cards */}
      <div className="hero-parallax-strong absolute inset-0 pointer-events-none hidden lg:block">
        {appCards.map((card) => (
          <div
            key={card.title}
            className="hero-app-card absolute liquid-glass rounded-2xl px-4 py-3 w-44"
            style={
              {
                top: card.top,
                left: card.left,
                '--rot': card.rot,
                transform: `rotate(${card.rot})`,
                animationDuration: `${card.dur}s`,
              } as React.CSSProperties
            }
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/20 text-primary-300 text-lg">
                <i className={card.icon}></i>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-white truncate">{card.title}</p>
                <p className="text-[10px] font-mono text-white/45 truncate">{card.sub}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="flex-1 min-h-screen px-6 md:px-12 lg:px-16 flex flex-col items-center justify-center text-center">
          <div className="w-full max-w-4xl flex flex-col items-center">
            <AnimatedHeading
              text={'A new standard for\ndigital programs.\nHANDLED'}
              className="text-4xl md:text-6xl lg:text-7xl font-normal mb-5 font-display text-white"
              delay={200}
              charDelay={26}
            />

            {/* Terminal-style subline */}
            <FadeIn delay={800} duration={900}>
              <div className="mb-6 inline-flex items-center gap-2 font-mono text-xs md:text-sm text-primary-300">
                <span className="text-white/40">$</span>
                <span>handled --deploy digital-assets</span>
                <span className="terminal-caret inline-block w-2 h-4 bg-primary-400 align-middle" />
              </div>
            </FadeIn>
          </div>
        </div>

        <FadeIn
          delay={1800}
          duration={1200}
          className="absolute bottom-3 left-0 right-0 flex justify-center"
        >
          <div className="flex flex-col items-center gap-1 text-white/40 text-[10px] font-mono uppercase tracking-[0.3em]">
            <span>scroll</span>
            <i className="ri-arrow-down-line animate-bounce"></i>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}